// lista zamówień
// @ts-nocheck

import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { format, parse } from 'date-fns';
import { ChevronRight, FiletypePdf, ThreeDotsVertical } from 'react-bootstrap-icons';
import { Trans, useTranslation } from 'react-i18next';
import classnames from 'classnames';

import { useRWD } from 'hooks';
import { reduxActions, useDispatch } from 'store';
import { useGetOrders, useGetOrdersUsers, useGetOrderExport } from 'api';
import { usePostOrderEditChangeStatus } from 'api/endpoints/usePostOrderEditChangeStatus';
import { IOrderListItem, IOrdersRequest, IOrdersUserListItem } from 'api/types';
import {
  SearchInput,
  PageTitle,
  Status,
  Button,
  Select,
  Loader,
  DateRangePicker,
  DropDown,
  Link,
  Modal
} from 'components/controls';
import Table, { IColumn } from 'components/controls/Table';
import { Link as AppLink } from 'components/controls';

import styles from 'theme/pages/Orders/Orders.module.scss';

const Orders = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { isMobile } = useRWD();
  const { pathname } = useLocation();

  // przyciski Akceptuj/Odrzuć tylko gdy API zwraca is_current_user_order_approval === true (pole z API, nie w core)
  const canAcceptOrder = (item: IOrderListItem) =>
    item.status_id === 10 && (item as any).is_current_user_order_approval === true;

  const getPosition = (string: string, subString: string, index: number) => {
    return string.split(subString, index).join(subString).length;
  };

  const urlPrefix = pathname.slice(0, getPosition(pathname, '/', 2));

  // domyślne parametry zapytania api o listę zamówień (sortowanie po dacie – najnowsze first)
  const defaultQueryParams = {
    page: 1,
    limit: 20,
    sort_method: 'CREATE_DATE_DESC'
  } as IOrdersRequest & { status?: number; key?: string; sort_method?: string };

  // ID wybranego zamówienia na potrzeby pobrania PDF
  const [chosenOrderId, setChosenOrderId] = useState<number | null>(null);

  // ID zamówienia do akceptacji/odrzucenia oraz typ akcji
  const [orderAction, setOrderAction] = useState<{ id: number; type: 'accept' | 'reject' } | null>(null);

  // hook do zmiany statusu zamówienia (akceptacja/odrzucenie)
  const { mutate: changeOrderStatus } = usePostOrderEditChangeStatus({
    onSuccess: () => {
      setOrderAction(null);
      refetchOrders();
    }
  });

  // parametry zapytania do API o listę zamówień
  const [queryParams, setQueryParams] = useState<
    IOrdersRequest & { status?: number; key?: string; sort_method?: string }
  >(defaultQueryParams);

  // pobranie listy zamówień
  const { data: ordersData, refetch: refetchOrders } = useGetOrders(queryParams);

  // pobranie listy użytkowników w zamówieniach
  const { data: ordersUsersData } = useGetOrdersUsers();

  // export zamówienia do pdf
  const { isFetching: isOrderExporting } = useGetOrderExport(
    chosenOrderId || 0,
    { export_type: 'pdf' },
    {
      enabled: !!chosenOrderId,
      onSuccess: (data) => {
        const a = document.createElement('a');
        a.download = data.file_name;
        a.href = `data:image/gif;base64,${data.content}`;
        a.click();
      },
      onSettled: () => setChosenOrderId(null)
    }
  );

  // ustawienie breadcrumbs'ów po zamontowaniu komponentu
  useEffect(() => {
    dispatch(
      reduxActions.setBreadcrumbs([
        { name: t('Dashboard'), path: '/dashboard' },
        { name: t('Lista zamówień') }
      ])
    );
  }, []);

  // funkcja resetująca filtry
  const resetFilters = () => setQueryParams(defaultQueryParams);

  // czy jest zastosowany jakiś extra filtr?
  const areFiltersApplied =
    !!queryParams.search_keyword ||
    !!queryParams.order_date_from ||
    !!queryParams.order_date_to ||
    !!queryParams.user_ordering ||
    !!queryParams.status ||
    !!queryParams.key;

  // opcje statusów (zbierane z danych listy)
  interface IStatusSelectItem { id: number; name: string }
  const statusOptions = useMemo(() => {
    const unique = new Map<number, { value: number; label: string; item: IStatusSelectItem }>();
    (ordersData?.items || []).forEach((it: any) => {
      if (it?.status_id && it?.status?.label) {
        unique.set(it.status_id, {
          value: it.status_id,
          label: it.status.label,
          item: { id: it.status_id, name: it.status.label }
        });
      }
    });
    return Array.from(unique.values());
  }, [ordersData]);

  // opcje w selektorze uźytkowników w filtrze
  const usersOptions = useMemo(
    () => ordersUsersData?.items.map((item) => ({ value: item.id, label: item.name, item })) || [],
    [ordersUsersData]
  );

  const renderExportButton = (id: number) =>
    isOrderExporting && chosenOrderId === id ? (
      <div className={styles.exportLoader}>
        <Loader />
      </div>
    ) : (
      <button
        className={styles.exportButton}
        onClick={() => !chosenOrderId && setChosenOrderId(id)}>
        <FiletypePdf /> <Trans>export</Trans>
      </button>
    );

  // kolumny tabelki z zamówieniami
  const columns: IColumn<IOrderListItem>[] = useMemo(
    () => [
      {
        title: <Trans>LP</Trans>,
        dataIndex: 'lp',
        align: 'center'
      },
      {
        title: <Trans>Symbol</Trans>,
        dataIndex: 'symbol',
        align: 'left',
        renderCell: (item) => (
          <AppLink
            className={styles.link}
            to={`${pathname.replace(`${urlPrefix}`, '')}/${item.id}`}>
            {item.symbol ?? item.id}
          </AppLink>
        )
      },
      {
        title: <Trans>Użytkownik</Trans>,
        dataIndex: 'user_name',
        align: 'left'
      },
      {
        title: <Trans>Status</Trans>,
        dataIndex: 'status',
        align: 'center',
        renderCell: (item) => (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
            <Status icon={''} message={item.status.label} color={item.status.color} />
            {canAcceptOrder(item) && (
              <div style={{ display: 'flex', gap: 8 }}>
                <Button
                  size="small"
                  color="primary"
                  style={{
                    padding: '6px 12px',
                    minWidth: 'auto',
                    fontSize: '12px'
                  }}
                  onClick={() => setOrderAction({ id: item.id, type: 'accept' })}>
                  <Trans>Akceptuj</Trans>
                </Button>
                <Button
                  size="small"
                  color="danger"
                  ghost
                  style={{
                    padding: '6px 12px',
                    minWidth: 'auto',
                    fontSize: '12px'
                  }}
                  onClick={() => setOrderAction({ id: item.id, type: 'reject' })}>
                  <Trans>Odrzuć</Trans>
                </Button>
              </div>
            )}
          </div>
        )
      },
      {
        title: <Trans>Liczba produktów</Trans>,
        dataIndex: 'products_total_count',
        align: 'center'
      },
      {
        title: <Trans>Data utworzenia</Trans>,
        dataIndex: 'create_date',
        align: 'center',
        renderCell: (item) => format(new Date(item.create_date), 'dd-MM-yyyy')
      },
      {
        title: <Trans>Wartość netto</Trans>,
        dataIndex: 'value_net',
        align: 'right',
        renderCell: (item) => `${item.value_net_formatted || '-'} ${item.currency}`
      },
      {
        title: <Trans>Wartość brutto</Trans>,
        dataIndex: 'value_gross',
        align: 'right',
        renderCell: (item) => `${item.value_gross_formatted || '-'} ${item.currency}`
      }
    ],
    [ordersUsersData, isOrderExporting, chosenOrderId]
  );

  return (
    <div className={classnames(styles.componentWrapper, 'StylePath-Pages-Orders')}>
      <PageTitle
        title={
          <>
            <Trans>Lista zamówień</Trans>{' '}
            <span className="thin">({ordersData?.total_count || 0})</span>
          </>
        }
      />
      <div className={styles.filtersWrapper}>
        {/* Pierwszy rząd filtrów */}
        <div className={styles.userSelectWrapper}>
          <SearchInput
            placeholder={t('Podaj symbol zamówienia')}
            value={queryParams.search_keyword}
            onChange={(value) =>
              setQueryParams((prevState) => ({ ...prevState, search_keyword: value, page: 1 }))
            }
          />
        </div>
        <div className={styles.userSelectWrapper}>
          <DateRangePicker
            dateRange={{
              from: queryParams.order_date_from
                ? format(parse(queryParams.order_date_from, 'dd-MM-yyyy', new Date()), 'yyyy-MM-dd')
                : undefined,
              to: queryParams.order_date_to
                ? format(parse(queryParams.order_date_to, 'dd-MM-yyyy', new Date()), 'yyyy-MM-dd')
                : undefined,
            }}
            onChange={(range) =>
              setQueryParams((prevState) => ({
                ...prevState,
                order_date_from: format(new Date(range.from), 'dd-MM-yyyy'),
                order_date_to: format(new Date(range.to), 'dd-MM-yyyy'),
              }))
            }
          />
        </div>
        <div className={styles.userSelectWrapper}>
          <Select<IOrdersUserListItem>
            variant="bordered"
            placeholder={`${t('Wybierz użytkownika')}...`}
            options={usersOptions}
            value={queryParams.user_ordering}
            onChange={(item) =>
              setQueryParams((prevState) => ({
                ...prevState,
                user_ordering: item?.id,
              }))
            }
          />
        </div>

        <div className={styles.userSelectWrapper}>
          <Select<IStatusSelectItem>
            variant="bordered"
            placeholder={`${t('Wybierz status')}...`}
            options={statusOptions}
            value={queryParams.status}
            onChange={(item) =>
              setQueryParams((prev) => ({ ...prev, status: item?.id, page: 1, key: undefined }))
            }
            clearable
          />
        </div>
        <div className={styles.userSelectWrapper}>
            <Button size="small" disabled={!areFiltersApplied} onClick={resetFilters}>
              {t('Wyczyść filtrowanie')}
            </Button>
        </div>
      </div>

      {isMobile ? (
        <div className={styles.mobileList}>
          {ordersData?.items.map((item) => (
            <div key={item.id} className={styles.mobileListItem}>
              <div className={styles.line}>
                <span>
                  {item.lp}
                  <Link
                    to={`${pathname.replace(`${urlPrefix}`, '')}/${item.id}`}
                    className={styles.primary}>
                    <Trans>Symbol</Trans> {item.symbol ?? item.id}
                  </Link>
                </span>

                <DropDown
                  label={<ThreeDotsVertical />}
                  items={[
                    {
                      label: renderExportButton(item.id)
                    }
                  ]}
                  withDropdownIcon={false}
                />
              </div>
              <div className={styles.line}>
                <span>
                  <span className={styles.label}>
                    <Trans>Użytkownik</Trans>:
                  </span>
                  {item.user_name}
                </span>
              </div>
              <div className={styles.line}>
                <span>
                  <span className={styles.label}>
                    <Trans>Liczba produktów</Trans>:
                  </span>{' '}
                  {item.products_total_count}
                </span>
              </div>
              <div className={styles.line}>
                <div>
                  <span className={styles.label}>
                    <Trans>Data utworzenia</Trans>
                  </span>{' '}
                  <br />
                  {format(new Date(item.create_date), 'dd-MM-yyyy')}
                </div>
                <Status
                  icon={''}
                  message={item.status.label}
                  color={item.status.color}
                />
              </div>
              <div className={styles.line}>
                <div>
                  <span className={styles.label}>
                    <Trans>Wartość netto</Trans>
                  </span>{' '}
                  <br />
                  {item.value_net_formatted} {item.currency}
                </div>

                <div className={styles.right}>
                  <span className={styles.label}>
                    <Trans>Wartość brutto</Trans>
                  </span>{' '}
                  <br />
                  <span className={styles.highlight}>
                    {item.value_gross_formatted} {item.currency}
                  </span>
                </div>
              </div>
              {canAcceptOrder(item) && (
                <div className={styles.line}>
                  <div />
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Button
                      size="small"
                      color="primary"
                      style={{
                        padding: '6px 12px',
                        minWidth: 'auto',
                        fontSize: '12px'
                      }}
                      onClick={() => setOrderAction({ id: item.id, type: 'accept' })}>
                      <Trans>Akceptuj</Trans>
                    </Button>
                    <Button
                      size="small"
                      color="danger"
                      ghost
                      style={{
                        padding: '6px 12px',
                        minWidth: 'auto',
                        fontSize: '12px'
                      }}
                      onClick={() => setOrderAction({ id: item.id, type: 'reject' })}>
                      <Trans>Odrzuć</Trans>
                    </Button>
                  </div>
                </div>
              )}
              <div className={styles.line}>
                <div />
                <Link
                  to={`${pathname.replace(`${urlPrefix}`, '')}/${item.id}`}
                  className={styles.link}>
                  <Trans>szczegóły zamówienia</Trans> <ChevronRight />
                </Link>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <Table<IOrderListItem>
          columns={columns}
          dataSource={ordersData?.items || []}
          rowKey="id"
          pagination={{
            page: queryParams.page || 1,
            pagesCount: ordersData?.total_pages || 1,
            onChange: (page) => setQueryParams((prevState) => ({ ...prevState, page }))
          }}
        />
      )}

      {!!orderAction && (
        <Modal
          title={orderAction.type === 'accept' ? t('Potwierdzenie akceptacji') : t('Potwierdzenie odrzucenia')}
          onClose={() => setOrderAction(null)}>
          <div className={styles.confirmationModal || ''} style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <span>
              {orderAction.type === 'accept' ? (
                <Trans>Czy na pewno chcesz zaakceptować zamówienie?</Trans>
              ) : (
                <Trans>Czy na pewno chcesz odrzucić zamówienie?</Trans>
              )}
            </span>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button ghost onClick={() => setOrderAction(null)}>
                <Trans>Anuluj</Trans>
              </Button>
              <Button
                color={orderAction.type === 'accept' ? 'primary' : 'danger'}
                onClick={() => {
                  changeOrderStatus({
                    orderId: orderAction.id,
                    data: { status: orderAction.type }
                  });
                }}>
                {orderAction.type === 'accept' ? <Trans>Akceptuj</Trans> : <Trans>Odrzuć</Trans>}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Orders;
